import { useAtom } from "jotai";
import { atomWithHash } from "jotai-location";
import React, { ReactNode, useState } from "react";
import { BsBorderMiddle } from "react-icons/bs";
import {
  FaMap,
  FaMapMarkedAlt,
  FaMapPin,
  FaSearchMinus,
  FaSearchPlus,
} from "react-icons/fa";
import LiveFilters from "./LiveFilters";
import { Count } from "./Count";
import { Map } from "./Map";
import { Pagination } from "./Pagination";
import { PerPage } from "./PerPage";
import { Results } from "./Results";
import { ISearchContext, SearchContextProvider } from "./SearchContext";
import SortContainer from "./SortContainer";

export interface ISearchProps {
  /**
   * An array of arbitrary items in the search index.
   */
  index: any[];
  /**
   * Classes to add to the search container div.
   */
  className?: string;
  /**
   * Function to render items in search results.
   */
  renderItem: (arg0: any, index: number) => React.ReactNode;

  /**
   * Results per page, or 0 for All
   */
  pageSize: number;

  /**
   * Optional list of page size options to expose to users.
   */
  pageSizeOpts?: number[];

  /**
   * Include a Google Map results?
   */
  useMap?: boolean;

  /**
   * Google Map mapId parameter
   */
  mapId?: string;

  /**
   * Include a Count component to show current number of results and page visible?
   */
  showCount?: boolean;

  /**
   * Show pager above results?
   */
  pagerTop?: boolean;

  /**
   * Show page below results?
   */
  pagerBottom?: boolean;

  /**
   * Filter nodes to display in the standard filters section of the search.
   */
  primaryFilters?: (context: ISearchContext) => React.ReactNode;

  /**
   * Filter nodes to display in the middle filters section of the search.
   */
  secondaryFilters?: (context: ISearchContext) => React.ReactNode;

  /**
   * Filter nodes to display in the collapsible advanced filters section of the search.
   */
  alternateFilters?: (context: ISearchContext) => React.ReactNode;

  /**
   * Class name(s) to apply to the filter wrapper
   */
  filterClassName?: string;
  /**
   * Class name(s) to apply to the header (sorts, counts, etc.) wrapper
   */
  headerClassName?: string;
  /**
   * Class name(s) to apply to the pagination control wrapper
   */
  pagerClassName?: string;
  /**
   * Class name(s) to apply to the map control wrapper
   */
  mapClassName?: string;
  /**
   * Class name(s) to apply to the results wrapper
   */
  resultsClassName?: string;
  /**
   * Text for the link to show advanced filters.
   */
  showAdvancedText?: string;

  /**
   * Text for the link to hide advanced filters.
   */
  hideAdvancedText?: string;

  /**
   * Hide alternate filters ("collapsible" advanced search).
   */
  hideAlternateFiters?: boolean;

  /**
   * Text for the link to show the map.
   */
  showMapText?: string;

  /**
   * Click handler to fire when a map marker for an item in the index
   * is clicked.
   */
  mapClick?: (arg: any) => void;

  /**
   * Text for the link to hide the map.
   */
  hideMapText?: string;

  /**
   * Configured Sort component for exposed sort options in the search.
   */
  sort?: (context: ISearchContext) => React.ReactNode;
  /**
   * Initial Filter if provided
   */
  initialFilter?: string
  /**
   * Default sort if provided
   */
  defaultSort?: string;
  /**
   * Search form background image
   */
  formBackgroundImage?: string;
  /**
   * Search form title
   */
  title?: string;
  renderFunction?: (arg0: any) => void;
  preRenderItem?: (arg0: any, index: number) => void;
  headerChildren?: React.ReactNode;
  onPageChangeScrollTo?: () => void;
}

export const Search: React.FC<ISearchProps> = ({
  index,
  renderItem,
  useMap = false,
  mapClick,
  pageSize,
  pageSizeOpts,
  showCount = false,
  pagerBottom = false,
  pagerTop = false,
  primaryFilters,
  secondaryFilters,
  alternateFilters,
  showMapText = "Show Map",
  hideMapText = "Hide Map",
  sort,
  mapId,
  className,
  filterClassName,
  headerClassName,
  pagerClassName,
  resultsClassName,
  mapClassName,
  initialFilter,
  defaultSort,
  hideAlternateFiters = false,
  showAdvancedText,
  hideAdvancedText,
  formBackgroundImage,
  title,
  renderFunction,
  preRenderItem,
  headerChildren,
  onPageChangeScrollTo
}) => {
  const [showMapAtom, setShowMapAtom] = useState<any>(() =>
    atomWithHash("showMap", false, { setHash: "replaceState" })
  );
  const [showMap, setShowMap]: any = useAtom(showMapAtom);
  /*Page size type check*/
  pageSize = typeof pageSize === "string" ? parseInt(pageSize) : pageSize;
  return (
    <SearchContextProvider key={(typeof window !== "undefined" ? window.location.hash : "")} index={index} pageSize={pageSize} initialFilter={initialFilter} initialSort={defaultSort}>
      <div className={`search ${className}`}>
        <LiveFilters
          className={filterClassName}
          primaryFilters={primaryFilters}
          secondaryFilters={secondaryFilters}
          alternateFilters={alternateFilters}
          hideAlternateFilters={hideAlternateFiters}
          showAdvancedText={showAdvancedText}
          hideAdvancedText={hideAdvancedText}
          formBackgroundImage={formBackgroundImage}
          title={title}
        />
        <div
          className={`gap-2 sm:flex sm:justify-between ${headerClassName} search-header`}
        >
          {pageSizeOpts && (
            <PerPage className="text-center py-1" opts={pageSizeOpts} />
          )}
          {showCount && <Count className="text-center py-1" />}
          <SortContainer
            sort={sort}
          />
          {useMap && (
            <div className="text-center">
              <a
                onClick={(event) => {
                  setShowMap(!showMap);
                }}
                className="cursor-pointer"
                title={showMap ? hideMapText : showMapText}
              >
                <FaMapMarkedAlt className="inline-block mr-1.5 text-xl" />
                {showMap ? hideMapText : showMapText}
              </a>
            </div>
          )}
          {headerChildren}
        </div>
        {pagerTop && (
          <Pagination
            className={`my-6 ${pagerClassName} pagination pagination-top`}
            onPageChangeScrollTo={onPageChangeScrollTo}
          />
        )}
        {useMap && showMap && (
          <Map
            mapId={mapId}
            className={`my-6 ${mapClassName} search-map`}
            mapClick={mapClick}
          />
        )}
        <Results
          className={`flex flex-wrap my-6 place-content-evenly ${resultsClassName} search-results`}
          renderItem={renderItem}
          renderFunction={renderFunction}
          preRenderItem={preRenderItem}
        />
        {pagerBottom && (
          <Pagination
            className={`my-6 ${pagerClassName} pagination pagination-bottom`}
            onPageChangeScrollTo={onPageChangeScrollTo}
          />
        )}
      </div>
    </SearchContextProvider>
  );
};

export default Search;
